/**
 * Stripe-specific Error Boundary
 *
 * Wraps Stripe-related components and displays a configuration error
 * if Stripe environment variables are missing. Uses the existing
 * FeatureErrorBoundary infrastructure with a custom fallback that
 * shows configuration-specific error details.
 */

import { Component, ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';
import { FeatureErrorBoundary } from './ErrorBoundary/FeatureErrorBoundary';
import { StripeConfigError } from '../config/stripe';

interface StripeErrorBoundaryProps {
  children: ReactNode;
  featureName?: string;
}

/**
 * Configuration error fallback component
 * Shows detailed error information for missing Stripe environment variables
 */
const ConfigurationErrorFallback = ({ error }: { error?: Error | null }) => {
  const missingVars = error instanceof StripeConfigError ? error.missingVars : [];

  return (
    <div className='flex items-center justify-center min-h-[400px] p-8'>
      <div className='max-w-2xl w-full bg-white rounded-lg shadow-lg p-8'>
        <div className='flex items-center gap-3 mb-4'>
          <AlertCircle className='w-8 h-8 text-red-500' />
          <h1 className='text-2xl font-bold text-gray-900'>Configuration Error</h1>
        </div>

        <div className='mb-6'>
          <p className='text-gray-700 mb-4'>
            The payment feature is missing required configuration. Please ensure all required
            environment variables are set.
          </p>

          <div className='bg-red-50 border border-red-200 rounded-md p-4 mb-4'>
            <p className='text-sm font-semibold text-red-800 mb-2'>Error Details:</p>
            <p className='text-sm text-red-700'>{error?.message || 'Configuration error'}</p>
          </div>

          {missingVars.length > 0 && (
            <div className='bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4'>
              <p className='text-sm font-semibold text-yellow-800 mb-2'>
                Missing Environment Variables:
              </p>
              <ul className='list-disc list-inside space-y-1'>
                {missingVars.map((varName) => (
                  <li key={varName} className='text-sm text-yellow-700 font-mono'>
                    {varName}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className='mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md'>
            <p className='text-sm font-semibold text-blue-800 mb-2'>How to Fix:</p>
            <ol className='list-decimal list-inside space-y-2 text-sm text-primary'>
              <li>
                Check your <code className='bg-blue-100 px-1 rounded'>.env</code> file or
                environment configuration
              </li>
              <li>Ensure all Stripe-related environment variables are set with valid values</li>
              <li>
                Restart the development server or rebuild the application after updating environment
                variables
              </li>
              <li>
                For production deployments, verify that environment variables are properly
                configured in your hosting platform
              </li>
            </ol>
          </div>
        </div>

        <div className='flex gap-3'>
          <button
            onClick={() => window.location.reload()}
            className='px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors'
          >
            Reload Page
          </button>
          <a
            href='https://stripe.com/docs'
            target='_blank'
            rel='noopener noreferrer'
            className='px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors'
          >
            Stripe Documentation
          </a>
        </div>
      </div>
    </div>
  );
};

/**
 * Custom error boundary that catches StripeConfigError and shows configuration error UI
 */
class StripeConfigErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode; fallback: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    if (error instanceof StripeConfigError) {
      return { hasError: true, error };
    }
    // Re-throw non-StripeConfigError errors
    throw error;
  }

  componentDidCatch(error: Error) {
    if (error instanceof StripeConfigError) {
      console.error('Stripe configuration error:', error);
      console.error('Missing environment variables:', error.missingVars);
    }
  }

  render() {
    if (this.state.hasError && this.state.error instanceof StripeConfigError) {
      return <ConfigurationErrorFallback error={this.state.error} />;
    }
    return this.props.children;
  }
}

/**
 * Wrapper component that uses FeatureErrorBoundary with custom Stripe error handling
 * Falls back to configuration error UI for StripeConfigError, otherwise uses default FeatureErrorBoundary UI
 */
export function StripeFeatureErrorBoundary({
  children,
  featureName = 'Payment',
}: StripeErrorBoundaryProps) {
  return (
    <StripeConfigErrorBoundary fallback={<ConfigurationErrorFallback />}>
      <FeatureErrorBoundary
        featureName={featureName}
        showErrorDetails={process.env.NODE_ENV === 'development'}
        allowDismiss={false}
        onError={(error) => {
          if (error instanceof StripeConfigError) {
            console.error('Stripe configuration error:', error);
            console.error('Missing environment variables:', error.missingVars);
          }
        }}
      >
        {children}
      </FeatureErrorBoundary>
    </StripeConfigErrorBoundary>
  );
}
